import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { LoadingController } from 'ionic-angular';
import { TranslateService } from '@ngx-translate/core';
import { ServerService } from '../../services/server-service';
import { ToastService } from '../../services/toast-service';
import {
  DEFAULT_APP_QUESTIONNAIRE_QUESTIONS,
  DEFAULT_OPEN_FEEDBACK,
  QuestionnaireItem,
} from './app-questionnaire.model';

@Component({
  selector: 'app-questionnaire',
  templateUrl: 'app-questionnaire.html',
})
export class AppQuestionnaireComponent implements OnInit {
  @Input() submitEndpoint: string = 'forms/submitAppQuestionnaire.php';
  @Input() submittedStorageKey: string = 'appQuestionnaireSubmittedV1';
  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() mandatoryNotice: string = '';
  @Input() heroImage: string = 'assets/imgs/questionnaire-stars.svg';
  @Input() questions: QuestionnaireItem[] = DEFAULT_APP_QUESTIONNAIRE_QUESTIONS;
  @Input() openFeedbackTitle: string = DEFAULT_OPEN_FEEDBACK.title;
  @Input() openFeedbackPlaceholder: string = DEFAULT_OPEN_FEEDBACK.placeholder;

  @Output() submitted = new EventEmitter<any>();
  @Output() closed = new EventEmitter<void>();

  readonly stars: number[] = [1, 2, 3, 4, 5];
  ratings: { [key: string]: number } = {};
  suggestedChange: string = '';
  submitting: boolean = false;

  displayTitle: string = '';
  displaySubtitle: string = '';
  displayMandatory: string = '';
  displayQuestions: QuestionnaireItem[] = [];
  displayOpenTitle: string = '';
  displayOpenPlaceholder: string = '';

  constructor(
    private api: ServerService,
    private toast: ToastService,
    public loadingCtrl: LoadingController,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.resolveDisplayCopy();
  }

  private resolveDisplayCopy(): void {
    this.displayTitle = this.title || this.translate.instant('AppQuestionnaire.title');
    this.displaySubtitle = this.subtitle || this.translate.instant('AppQuestionnaire.subtitle');
    this.displayMandatory = this.mandatoryNotice || this.translate.instant('AppQuestionnaire.mandatory');
    this.displayOpenTitle = this.openFeedbackTitle.indexOf('AppQuestionnaire.') === 0
      ? this.translate.instant(this.openFeedbackTitle)
      : (this.openFeedbackTitle || this.translate.instant('AppQuestionnaire.openTitle'));
    this.displayOpenPlaceholder = this.openFeedbackPlaceholder.indexOf('AppQuestionnaire.') === 0
      ? this.translate.instant(this.openFeedbackPlaceholder)
      : (this.openFeedbackPlaceholder || this.translate.instant('AppQuestionnaire.openPlaceholder'));
    this.displayQuestions = (this.questions || []).map((q) => ({
      key: q.key,
      title: (q.title && q.title.indexOf('AppQuestionnaire.') === 0)
        ? this.translate.instant(q.title)
        : q.title,
    }));
  }

  setRating(key: string, value: number): void {
    this.ratings[key] = value;
  }

  isRated(key: string): boolean {
    const value = this.ratings[key];
    return typeof value === 'number' && value >= 1 && value <= 5;
  }

  allRatingsComplete(): boolean {
    return this.displayQuestions.every((q) => this.isRated(q.key));
  }

  allFieldsComplete(): boolean {
    return this.allRatingsComplete();
  }

  close(): void {
    this.closed.emit();
  }

  submitQuestionnaire(): void {
    if (!this.allRatingsComplete()) {
      this.toast.presentToast(this.translate.instant('AppQuestionnaire.rateAll'));
      return;
    }

    const object = {
      personalID: localStorage.getItem('user_id'),
      mercazAllId: localStorage.getItem('mercazAllId'),
      pqpq: localStorage.getItem('server'),
      pushToken: localStorage.getItem('push'),
      dailyProductivity: this.ratings['dailyProductivity'],
      uiIntuitive: this.ratings['uiIntuitive'],
      stabilitySpeed: this.ratings['stabilitySpeed'],
      functionalityNeeds: this.ratings['functionalityNeeds'],
      recommendToPeer: this.ratings['recommendToPeer'],
      suggestedChange: (this.suggestedChange || '').trim(),
    };

    const loading = this.loadingCtrl.create({
      content: this.translate.instant('AppQuestionnaire.sending'),
      spinner: 'bubbles',
    });
    this.submitting = true;
    loading.present();

    this.api.sendPostParams(this.submitEndpoint, object).then((data) => {
      loading.dismiss();
      this.submitting = false;

      if (data && data['sucsses']) {
        try {
          localStorage.setItem(this.submittedStorageKey, '1');
        } catch (e) { /* noop */ }
        this.toast.presentToast(this.translate.instant('AppQuestionnaire.thanks'));
        this.submitted.emit({ data, payload: object });
        return;
      }

      this.toast.presentToast((data && data['error']) || this.translate.instant('COMMON.systemErrorRetry'));
    }, () => {
      loading.dismiss();
      this.submitting = false;
      this.toast.presentToast(this.translate.instant('COMMON.commErrorRetry'));
    });
  }
}



// WEBPACK FOOTER //
// ./src/components/app-questionnaire/app-questionnaire.ts